import DecreasedRiskBadge from '@/app/components/risks/common/badges/decreased-risk-badge';
import IncreasedRiskBadge from '@/app/components/risks/common/badges/increased-risk-badge';
import { TypeOfCancer } from '@/components/icons/icon-catch/types';
import { RiskFactorType } from '@/repositories/risk.repository';
import { formatRiskDecimal } from '@/utils';
import { Box, Button, Flex, FlexProps, Text } from '@cmpsr/components';

type RiskFactorCardData = {
  impact: number;
  shortDescription: string;
  conditions?: TypeOfCancer[];
  riskFactorType: RiskFactorType;
};

type RiskFactorCardProps = FlexProps & {
  factor: RiskFactorCardData;
  onClick: () => void;
};

export default function RiskFactorCard({ factor, onClick, ...props }: RiskFactorCardProps) {
  const { impact, shortDescription } = factor;
  const isNonQuantifiable =
    factor.riskFactorType === RiskFactorType.nonQuantifiableNegative ||
    factor.riskFactorType === RiskFactorType.nonQuantifiablePositive;

  const renderBadge = () => {
    const formattedDecreasedRisk = impact < 0 ? Math.abs(impact) : impact;
    const isPositive = factor.riskFactorType === RiskFactorType.nonQuantifiableNegative || impact > 0;
    const BadgeComponent = isPositive ? IncreasedRiskBadge : DecreasedRiskBadge;
    const percentage = isNonQuantifiable ? '' : formatRiskDecimal(isPositive ? impact : formattedDecreasedRisk);

    return <BadgeComponent percentage={percentage} isNonQuantifiable={isNonQuantifiable} />;
  };

  return (
    <Button
      aria-label="Clickable Risk Factor"
      variant="link-accent"
      justifyContent="flex-start"
      position="relative"
      py="spacer-2"
      px="spacer-1"
      background={{ bg: 'background-container-default' }}
      mx="-4px"
      borderRadius="radii-2"
      _hover={{ bg: 'background-container-hover' }}
      _active={{ bg: 'background-container-pressed' }}
      _focus={{ boxShadow: 'none' }}
      onClick={onClick}
    >
      <Flex gap="spacer-4" alignItems="center" {...props}>
        <Box w="60px" textAlign="left">
          {renderBadge()}
        </Box>

        <Text variant="text-body-meta-regular" flex="1" whiteSpace="normal" textAlign="left">
          {shortDescription}
        </Text>
      </Flex>
    </Button>
  );
}
